import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Smartphone, 
  Download, 
  CheckCircle, 
  ArrowRight, 
  Share, 
  PlusSquare,
  User,
  Zap,
  Bell,
  X,
  FileText,
  Shield
} from 'lucide-react';

const InstallClientApp = () => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Enregistrer le Service Worker Client
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw-client.js', { scope: '/' })
        .then((registration) => {
        })
        .catch((error) => {
          console.error('[PWA Client] Erreur enregistrement SW:', error);
        });
    }

    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isAndroidDevice = /android/i.test(userAgent);
    const isMobileDevice = isIOSDevice || isAndroidDevice || /mobile/i.test(userAgent);
    
    setIsIOS(isIOSDevice);
    setIsMobile(isMobileDevice);

    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } else if (isIOS) {
      setShowIOSInstructions(true);
    }
  };

  const goToClient = () => {
    navigate('/client-dashboard');
  };

  if (isInstalled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Application installée !</h1>
          <p className="text-slate-600 mb-6">
            L'application Mon Espace ASAR est déjà installée sur votre appareil.
          </p>
          <button
            onClick={goToClient}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center gap-2"
          >
            Accéder à mon espace
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <div className="p-6 text-center pt-12">
        <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <User size={40} className="text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Mon Espace ASAR</h1>
        <p className="text-blue-200">Votre espace client personnel</p>
      </div>

      {/* Main Content */}
      <div className="px-4 pb-8">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md mx-auto overflow-hidden">
          
          {/* Features */}
          <div className="p-6 border-b border-slate-100">
            <h2 className="font-semibold text-slate-900 mb-4">Fonctionnalités</h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Zap size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Accès rapide</p>
                  <p className="text-sm text-slate-500">Consultez vos contrats en un clic</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <FileText size={20} className="text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Vos documents</p>
                  <p className="text-sm text-slate-500">Accédez à tous vos documents</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Bell size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Notifications</p>
                  <p className="text-sm text-slate-500">Soyez alerté des échéances</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Shield size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Sécurisé</p>
                  <p className="text-sm text-slate-500">Vos données sont protégées</p>
                </div>
              </div>
            </div>
          </div>

          {/* Install Button */}
          <div className="p-6">
            {deferredPrompt ? (
              <button
                onClick={handleInstallClick}
                className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center gap-3 shadow-lg"
              >
                <Download size={24} />
                Installer l'application
              </button>
            ) : isIOS ? (
              <button
                onClick={handleInstallClick}
                className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center gap-3 shadow-lg"
              >
                <Download size={24} />
                Installer sur iPhone/iPad
              </button>
            ) : (
              <div className="text-center">
                <p className="text-slate-600 mb-4">
                  {isMobile 
                    ? "Votre navigateur va proposer l'installation automatiquement."
                    : "Scannez le QR Code depuis votre mobile pour installer l'app."}
                </p>
                <button
                  onClick={goToClient}
                  className="w-full py-4 bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-xl font-semibold hover:from-slate-800 hover:to-slate-900 transition-all flex items-center justify-center gap-2"
                >
                  Continuer vers mon espace
                  <ArrowRight size={20} />
                </button>
              </div>
            )}

            {(deferredPrompt || isIOS) && (
              <button
                onClick={goToClient}
                className="w-full mt-3 py-3 text-slate-600 hover:text-slate-900 font-medium transition-colors"
              >
                Continuer sans installer →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-4 border-b border-slate-100 flex items-center justify-between rounded-t-3xl">
              <h2 className="text-lg font-bold text-slate-900">Installation sur iOS</h2>
              <button
                onClick={() => setShowIOSInstructions(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={24} className="text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold">
                  1
                </div>
                <div>
                  <p className="font-semibold text-slate-900 mb-2">
                    Appuyez sur le bouton Partager
                  </p>
                  <div className="bg-slate-100 rounded-xl p-4 flex items-center justify-center">
                    <Share size={32} className="text-blue-500" />
                  </div>
                  <p className="text-sm text-slate-500 mt-2">
                    En bas de l'écran dans Safari
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold">
                  2
                </div>
                <div>
                  <p className="font-semibold text-slate-900 mb-2">
                    Faites défiler et appuyez sur
                  </p>
                  <div className="bg-slate-100 rounded-xl p-4 flex items-center gap-3">
                    <PlusSquare size={28} className="text-slate-700" />
                    <span className="font-medium text-slate-900">Sur l'écran d'accueil</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold">
                  3
                </div>
                <div>
                  <p className="font-semibold text-slate-900 mb-2">
                    Confirmez en appuyant sur "Ajouter"
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                    <CheckCircle size={32} className="text-blue-600 mx-auto mb-2" />
                    <p className="text-blue-700 text-sm">
                      L'app apparaîtra sur votre écran d'accueil
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-slate-100">
              <button
                onClick={() => setShowIOSInstructions(false)}
                className="w-full py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
              >
                J'ai compris
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallClientApp;
